import { useState, useRef, useCallback } from 'react';

const COMMON_MEDIA_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'avif', 'svg', 'bmp',
  'mp4', 'mov', 'm4v', 'webm', 'ogv', 'avi', 'mkv', '3gp'
]);

export interface UseFileDropOptions {
  /** Callback triggered when valid files are dropped. */
  onDropFiles: (files: File[]) => void | Promise<void>;
  /** Accepted MIME types or prefixes (default: ['image/', 'video/']). */
  accept?: string[];
  /** Whether dropping is disabled. */
  disabled?: boolean;
}

export interface UseFileDropReturn {
  isDraggingOver: boolean;
  dropProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

export function useFileDrop({
  onDropFiles,
  accept = ['image/', 'video/'],
  disabled = false,
}: UseFileDropOptions): UseFileDropReturn {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);

  const isAccepted = useCallback(
    (file: File): boolean => {
      if (!accept || accept.length === 0) return true;

      const fileType = (file.type || '').toLowerCase();
      const ext = file.name.split('.').pop()?.toLowerCase() || '';

      // Check against specified accept list
      for (const pattern of accept) {
        const p = pattern.toLowerCase();
        if (p.endsWith('/*')) {
          const prefix = p.slice(0, -1);
          if (fileType.startsWith(prefix)) return true;
        } else if (p.endsWith('/')) {
          if (fileType.startsWith(p)) return true;
        } else if (p.startsWith('.')) {
          if (ext === p.slice(1)) return true;
        } else if (fileType === p) {
          return true;
        }
      }

      // Fallback: If browser leaves file.type empty (common for HEIC/MOV on some platforms),
      // match against known common extensions if image/ or video/ is in accept list.
      const acceptsImageOrVideo = accept.some(
        (p) => p.startsWith('image') || p.startsWith('video')
      );
      if (acceptsImageOrVideo && ext && COMMON_MEDIA_EXTENSIONS.has(ext)) {
        return true;
      }

      return false;
    },
    [accept]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      dragCounterRef.current += 1;
      if (e.dataTransfer?.types && Array.from(e.dataTransfer.types).includes('Files')) {
        setIsDraggingOver(true);
      }
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      if (!isDraggingOver) {
        setIsDraggingOver(true);
      }
    },
    [disabled, isDraggingOver]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;

      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDraggingOver(false);
      }
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
      if (disabled) return;

      const rawFiles = Array.from(e.dataTransfer?.files ?? []);
      const acceptedFiles = rawFiles.filter(isAccepted);
      if (acceptedFiles.length > 0) {
        void onDropFiles(acceptedFiles);
      }
    },
    [disabled, isAccepted, onDropFiles]
  );

  return {
    isDraggingOver,
    dropProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
